import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from 'src/core/services/user.service';
import { UserCreatePayload } from 'src/interfaces/user';
import { companyDetails } from 'src/interfaces/company';
import { Individual, IndividualType } from 'src/interfaces/individual';
import { CompanyService } from 'src/core/services/company.service';
import { IndividualService } from 'src/core/services/individual.service';
import { SharedService } from 'src/core/services/shared.service';
import { AlertService } from 'src/core/services/alert.service';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { Observable, of, from } from 'rxjs';
import { startWith, map, debounceTime, switchMap, catchError } from 'rxjs/operators';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
    selector: 'app-user-create',
    templateUrl: './user-create.component.html',
    styleUrls: ['./user-create.component.scss'],
    standalone: false
})
export class UserCreateComponent implements OnInit {

  createUserForm = this._formBuilder.group({
    email: ['', Validators.compose([Validators.required, Validators.email])],
    name: ['', Validators.required],
    phone_number: ['', Validators.pattern(/^\+[0-9]{11,12}$/)],
    roles: [''],
    companySearch: [''],
    individualSearch: [''],
  });

  roles: string[] = [];
  allRoles: string[] = [
    'SuperAdmin','OperationsManager','PrescriptionManager', 'Fulfilment', 'Packaging', 'Prescription','Customer','Viewer'
  ];
  filteredRoles: Observable<string[]>;
  
  companies: string[] = [];
  /** Company id when user is linked to a company. */
  selectedCompanyId: number | null = null;
  /** Individual id when user is linked to an individual. */
  selectedIndividualId: number | null = null;
  /** Whether the linked entity is a company or an individual. */
  linkType: 'company' | 'individual' = 'company';
  /** For individual search list: resolve type label from individual_type_id. */
  individualTypes: IndividualType[] = [];
  /** Options from API search; emitted on each search term change. */
  filteredCompanies: Observable<companyDetails[]>;
  /** Options from API search for individuals. */
  filteredIndividuals: Observable<Individual[]>;
  
  selectable = true;
  removable = true;
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  @ViewChild('roleInput') roleInput: ElementRef<HTMLInputElement>;
  @ViewChild('companyInput') companyInput: ElementRef<HTMLInputElement>;
  @ViewChild('individualInput') individualInput: ElementRef<HTMLInputElement>;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _userService: UserService,
    private _companyService: CompanyService,
    private _individualService: IndividualService,
    private _sharedService: SharedService,
    private _alertService: AlertService,
    private _router: Router
  ) {}

  ngOnInit(): void {
    this.createUserForm.patchValue({
      email: "",
      password: "",
      name: "",
      phone_number: ""
    });

    (async () => {
      const data = await this._sharedService.getFormData(['individual_type']);
      this.individualTypes = data?.individual_type ?? [];
    })();

    // Company search: driven by companySearch control only
    this.filteredCompanies = this.createUserForm.controls['companySearch'].valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap((searchText: string) =>
        from(
          this._companyService.getCompanies(1, 50, searchText?.trim() ?? '', null, 'asc', 'name')
        ).pipe(
          map((list) => list.map((c) => ({ id: c.id, name: c.name }))),
          catchError(() => of([]))
        )
      )
    );

    // Individual search: driven by individualSearch control only
    this.filteredIndividuals = this.createUserForm.controls['individualSearch'].valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap((searchText: string) =>
        from(
          this._individualService.getIndividuals(1, 50, null, searchText?.trim() ?? null, null, 'asc', 'first_name')
        ).pipe(catchError(() => of([])))
      )
    );

    this.filteredRoles = this.createUserForm.controls['roles'].valueChanges.pipe(
      startWith(''),
      map((role: string) => this._filterRoles(role)));
  }

  private _filterRoles(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.allRoles.filter(role => role.toLowerCase().includes(filterValue));
  }

  /** Label for individual type in search list (title + name + type). */
  getIndividualTypeLabel(ind: Individual): string {
    if (!ind?.individual_type_id) return '-';
    const t = this.individualTypes.find((type) => type.id === ind.individual_type_id);
    return t ? t.type : '-';
  }

  async save() {
    const payload: UserCreatePayload = {
      email: this.createUserForm.value.email,
      roles: this.roles,
      name: this.createUserForm.value.name,
      phone: this.createUserForm.value.phone_number,
      company: this.linkType === 'company' ? (this.selectedCompanyId ?? undefined) : undefined,
      individual_id: this.linkType === 'individual' ? (this.selectedIndividualId ?? undefined) : undefined
    };

    const success = await this._userService.createUser(payload);

    if (success) this._router.navigate(['/portal/users']);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (this.roles.indexOf(event.option.viewValue) === -1) {
      this.roles.push(event.option.viewValue);
    }
    this.roleInput.nativeElement.value = '';
    this.createUserForm.controls['roles'].setValue('');
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    // Add our fruit
    if (value) {
      this.roles.push(value);
    }

    // Clear the input value
    event.chipInput!.clear();
  }

  remove(role: string): void {
    const index = this.roles.indexOf(role);

    if (index >= 0) {
      this.roles.splice(index, 1);
    }
  }

  selectedCompany(event: MatAutocompleteSelectedEvent): void {
    const company = event.option.value as companyDetails;
    if (
      company?.id != null &&
      this.companies.indexOf(event.option.viewValue) === -1 &&
      this.companies.length === 0
    ) {
      this.linkType = 'company';
      this.companies.push(event.option.viewValue);
      this.selectedCompanyId = company.id;
      this.selectedIndividualId = null;
    }
    this.createUserForm.controls['companySearch'].setValue('');
    if (this.companyInput?.nativeElement) this.companyInput.nativeElement.value = '';
    this.selectable = this.companies.length === 0;
  }

  selectedIndividual(event: MatAutocompleteSelectedEvent): void {
    const individual = event.option.value as Individual;
    if (individual?.id == null || this.companies.length > 0) return;
    const displayName = `${individual.first_name} ${individual.last_name}`.trim();
    if (this.companies.indexOf(displayName) === -1) {
      this.linkType = 'individual';
      this.companies = [displayName];
      this.selectedIndividualId = individual.id;
      this.selectedCompanyId = null;
    }
    this.createUserForm.controls['individualSearch'].setValue('');
    if (this.individualInput?.nativeElement) this.individualInput.nativeElement.value = '';
    this.selectable = this.companies.length === 0;
  }

  /** Switch between company and individual; clears current selection and search. */
  setLinkType(type: 'company' | 'individual'): void {
    if (this.linkType === type) return;
    this.linkType = type;
    this.companies = [];
    this.selectedCompanyId = null;
    this.selectedIndividualId = null;
    this.createUserForm.controls['companySearch'].setValue('');
    this.createUserForm.controls['individualSearch'].setValue('');
    this.selectable = true;
  }

  /** Only allow adding via autocomplete selection; reject free text. */
  addCompany(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value && this.companies.length === 0) {
      const msg = this.linkType === 'company'
        ? 'Please select a company from the list.'
        : 'Please select an individual from the list.';
      this._alertService.infoAlert(msg);
    }
    event.chipInput!.clear();
    if (this.linkType === 'company') {
      this.createUserForm.controls['companySearch'].setValue('');
      if (this.companyInput?.nativeElement) this.companyInput.nativeElement.value = '';
    } else {
      this.createUserForm.controls['individualSearch'].setValue('');
      if (this.individualInput?.nativeElement) this.individualInput.nativeElement.value = '';
    }
    this.selectable = this.companies.length === 0;
  }

  removeCompany(company: string): void {
    const index = this.companies.indexOf(company);
    if (index >= 0) {
      this.companies.splice(index, 1);
    }
    if (this.companies.length === 0) {
      this.selectedCompanyId = null;
      this.selectedIndividualId = null;
    }
    this.selectable = this.companies.length === 0;
  }

  isCompanySelected(): boolean {
    return this.companies.length > 0;
  }

}
